/**
 * Meta Pixel (Facebook) utility functions for client-side tracking
 *
 * These functions help track user events for Meta advertising campaigns.
 * All tracking only fires in production environment.
 */

// Helper to get cookie value
export function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        const cookieValue = parts.pop()?.split(';').shift();
        return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
}

// Generate a unique event ID for deduplication between client and server
export function generateEventId(eventName: string, userId?: string): string {
    const timestamp = Math.floor(Date.now() / 1000);
    const random = Math.random().toString(36).substring(2, 8);
    const userPart = userId ? `_${userId.substring(0, 8)}` : '';
    return `${eventName.toLowerCase()}${userPart}_${timestamp}_${random}`;
}

// Client-side: Track standard Meta Pixel event with optional event_id for deduplication
export function trackMetaEvent(
    eventName: string,
    params?: Record<string, any>,
    eventId?: string
) {
    if (process.env.NODE_ENV !== 'production') return;
    if (typeof window !== 'undefined' && window.fbq) {
        const eventParams = eventId ? { ...params, eventID: eventId } : params;
        window.fbq('track', eventName, eventParams);
    }
}

// Client-side: Track custom Meta event
export function trackMetaCustomEvent(
    eventName: string,
    params?: Record<string, any>
) {
    if (process.env.NODE_ENV !== 'production') return;
    if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', eventName, params);
    }
}

// Get attribution data for server-side calls
// Uses the stored click timestamp for proper FBC format
export function getMetaAttributionData(): { fbp: string | null; fbc: string | null } {
    const fbp = getCookie('fbp') || getCookie('_fbp');
    const fbclid = getCookie('meta_fbclid');

    // Use the stored click timestamp if available, otherwise use current time
    // Meta requires the original click timestamp for proper attribution
    const fbclidTimestamp = getCookie('meta_fbclid_ts');
    const parsedTimestamp = fbclidTimestamp ? parseInt(fbclidTimestamp, 10) : null;
    // Validate timestamp is a valid number (not NaN) and reasonable (not in future, not too old)
    const now = Math.floor(Date.now() / 1000);
    const clickTime = (parsedTimestamp && !isNaN(parsedTimestamp) && parsedTimestamp <= now && parsedTimestamp > now - 7776000)
        ? parsedTimestamp
        : now;

    const fbc = fbclid ? `fb.1.${clickTime}.${fbclid}` : null;
    return { fbp, fbc };
}

// Send server-side conversion event (for CompleteRegistration, Purchase, etc.)
export async function sendMetaConversion({
    eventName,
    email,
    userId,
    value,
    currency = 'USD',
    contentName,
    contentCategory,
    eventId,
}: {
    eventName: 'CompleteRegistration' | 'Purchase' | 'InitiateCheckout' | 'AddPaymentInfo';
    email: string;
    userId: string;
    value?: number;
    currency?: string;
    contentName?: string;
    contentCategory?: string;
    eventId?: string;
}): Promise<void> {
    if (process.env.NODE_ENV !== 'production') return;

    const { fbp, fbc } = getMetaAttributionData();

    // Generate event ID if not provided (for deduplication with client-side event)
    const finalEventId = eventId || generateEventId(eventName, userId);

    try {
        await fetch('/api/tracking/meta-conversion', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                eventName,
                email,
                userId,
                fbp,
                fbc,
                value,
                currency,
                contentName,
                contentCategory,
                eventId: finalEventId,
            }),
        });
    } catch (error) {
        console.error(`[Meta Pixel] Failed to send ${eventName} event:`, error);
    }
}

// Track event with both client-side and server-side for redundancy
// Uses the same event_id for deduplication
export async function trackMetaEventWithServer({
    eventName,
    email,
    userId,
    value,
    currency = 'USD',
    contentName,
    contentCategory,
    clientParams,
}: {
    eventName: 'CompleteRegistration' | 'Purchase' | 'InitiateCheckout' | 'AddPaymentInfo';
    email: string;
    userId: string;
    value?: number;
    currency?: string;
    contentName?: string;
    contentCategory?: string;
    clientParams?: Record<string, any>;
}): Promise<void> {
    if (process.env.NODE_ENV !== 'production') return;

    // Generate a shared event ID for deduplication
    const eventId = generateEventId(eventName, userId);

    // Fire client-side event
    trackMetaEvent(eventName, {
        ...clientParams,
        value,
        currency,
        content_name: contentName,
        content_category: contentCategory,
    }, eventId);

    // Fire server-side event with same event ID
    await sendMetaConversion({
        eventName,
        email,
        userId,
        value,
        currency,
        contentName,
        contentCategory,
        eventId,
    });
}
